<template>
	<div class="customer-reviews-bar">
		<dt class="customer-reviews-bar__header">
			<span
				class="customer-reviews-bar__header-title"
				v-text="title"
			/>
			<span
				class="customer-reviews-bar__header-rating"
				v-text="rating"
			/>
		</dt>
		<div
			role="progressbar"
			:aria-label="`${title} ${arialabelSuffix}`"
			class="customer-reviews-bar__progress"
			:aria-valuenow="value"
			aria-valuemin="0"
			:aria-valuemax="maxValue"
		/>
	</div>
</template>

<script lang="ts" setup>
import { RATING_MAX_VALUE } from '@global-js/constants';
import { computed } from 'vue';

interface Props {
	value: number;
	maxValue?: number;
	title?: string;
	rating?: string;
	arialabelSuffix?: string;
}

const { maxValue = RATING_MAX_VALUE, value, title, rating, arialabelSuffix = 'Bewertung' } = defineProps<Props>();

const currentValue = computed(() => Math.max(0, Math.min(value, maxValue)));

const progressStyle = computed(() => {
	const percentage = Math.floor((currentValue.value / maxValue) * 100);
	return `${percentage}%`;
});
</script>

<style lang="scss" scoped>
.customer-reviews-bar {
	&__header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 0.1rem;

		&-rating,
		&-title {
			font-size: $font-small-1;
			font-weight: $font-weight-semibold;
			line-height: 3.1rem;
		}

		&-title {
			@include text-ellipsis(100%);

			display: block;
		}

		&-rating {
			color: $color-link;
		}
	}

	&__progress {
		height: 0.4rem;
		position: relative;

		&::after,
		&::before {
			content: '';
			position: absolute;
			top: 0;
			bottom: 0;
			left: 0;
			border-radius: 1rem;
		}

		&::before {
			right: 0;
			background: $color-search-mask-border;
		}

		&::after {
			background: $customer-reviews-color;
			width: v-bind(progressStyle);
		}
	}
}
</style>
